import React, { useState, useContext, useRef, useMemo, useEffect } from 'react';
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import ClickOutside from '../ClickOutside';
import Popup from '../Popup';
import Container from '../Container';
import useMedia from '../../hooks/useMedia';
import IconAppMenu from './IconAppMenu';
import IconBusinessProfile from './IconBusinessProfile';
import IconLsOnline from './IconLsOnline';
import Text from '../Text';
import Anchor from '../Anchor';
import IconUserManagement from './IconUserManagement';
import IconWebsiteThemes from './IconWebsiteThemes';
import IconPerformio from './IconPerformio';
import IconStaffPortal from './IconStaffPortal';
import IconSalesToolkit from './IconSalesToolkit';
import IconLocalGPT from './IconLocalGPT';

const envUrls = {
  development: {
    localsearchOnline: 'https://development.ls-www.localsearch.cloud',
    localGPT: 'https://ls-local-gpt-client.development.localsearch.cloud',
    localsearchBusinessProfile:
      'https://development.ls-business-profile.localsearch.cloud',
    accountSettings: 'https://development.ls-user.localsearch.cloud',
    websiteThemes: 'https://websitethemes.localsearch.com.au',
    performio: 'https://localsearch.performio.co/Login.do',
    addAProfile:
      'https://development.ls-business-profile.localsearch.cloud/create-profile',
    salesToolkit: 'https://development.ls-sales-toolkit.localsearch.cloud',
  },
  staging: {
    localsearchOnline: 'https://staging.ls-www.localsearch.cloud',
    localGPT: 'https://ls-local-gpt-client.staging.localsearch.cloud',
    localsearchBusinessProfile:
      'https://staging.ls-business-profile.localsearch.cloud',
    accountSettings: 'https://staging.ls-user.localsearch.cloud',
    websiteThemes: 'https://websitethemes.localsearch.com.au',
    performio: 'https://localsearch.performio.co/Login.do',
    addAProfile:
      'https://staging.ls-business-profile.localsearch.cloud/create-profile',
    salesToolkit: 'https://staging.ls-sales-toolkit.localsearch.cloud',
  },
  production: {
    localsearchOnline: 'https://www.localsearch.com.au',
    localGPT: 'https://gpt.localsearch.com.au',
    localsearchBusinessProfile: 'https://business-profile.localsearch.com.au',
    accountSettings: 'https://account.localsearch.com.au',
    websiteThemes: 'https://websitethemes.localsearch.com.au',
    performio: 'https://localsearch.performio.co/Login.do',
    addAProfile: 'https://business-profile.localsearch.com.au/create-profile',
    salesToolkit: 'https://sales-toolkit.localsearch.com.au',
  },
};

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all ease 0.3s;
  background: none;
  border: none;
  width: 100%;
  height: 100%;
  &:focus {
    outline: none;
  }
`;

const StyledAnchor = styled(Anchor)`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 33.3333%;
  padding: 0 5px;
  margin-top: 15px;
  margin-bottom: 15px;
  flex-direction: column;
  border: none;
  background: none;
  &:hover span {
    color: ${props => props.theme.primary.normal};
  }
`;

const AppMenu = ({
  iconFill,
  iconFillActive,
  hide,
  env,
  onOpen,
  onClose,
  isStaff,
  isPersoniv,
  popupProps,
  ...props
}) => {
  const [show, setShow] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);
  const [popupHeight, setPopupHeight] = useState();

  const popupRef = useRef();

  const { mediaQueries } = useContext(ThemeContext);
  const { mobile } = useMedia(mediaQueries);

  const links = envUrls[env] || envUrls.production;

  useEffect(() => {
    if (show) {
      const { top, height } = popupRef.current.getBoundingClientRect();
      const maxHeight = window.innerHeight - top;
      if (height > maxHeight) {
        setPopupHeight(`${maxHeight}px`);
      }
    } else {
      setPopupHeight('auto');
    }
  }, [show]);

  const menuItems = useMemo(() => {
    const items = [
      {
        text: 'Localsearch',
        icon: <IconLsOnline />,
        href: links.localsearchOnline,
      },
      {
        text: 'Localsearch for Business',
        icon: <IconBusinessProfile />,
        href: links.localsearchBusinessProfile,
      },
      ...(isStaff || isPersoniv
        ? [
            {
              text: 'Add a Profile',
              icon: <IconStaffPortal />,
              href: links.addAProfile,
            },
          ]
        : []),
      ...(isStaff
        ? [
            {
              text: 'Local GPT',
              icon: <IconLocalGPT />,
              href: links.localGPT,
            },
          ]
        : []),
      ...(isStaff
        ? [
            {
              text: 'Sales Toolkit',
              icon: <IconSalesToolkit />,
              href: links.salesToolkit,
            },
          ]
        : []),
      ...(isStaff
        ? [
            {
              text: 'Website Themes',
              icon: <IconWebsiteThemes />,
              href: links.websiteThemes,
            },
          ]
        : []),
      ...(isStaff
        ? [
            {
              text: 'Performio',
              icon: <IconPerformio />,
              href: links.performio,
            },
          ]
        : []),
      {
        text: 'Account Settings',
        icon: <IconUserManagement />,
        href: links.accountSettings,
      },
    ];
    return items.filter(({ hidden }) => !hidden);
  }, [isStaff, isPersoniv]);

  return (
    <ClickOutside
      onClickOutside={() => {
        setShow(false);
        if (show) {
          onClose();
        }
      }}
      borderRadius="100%"
      backgroundColor="white"
      {...props}
    >
      <StyledButton
        onMouseEnter={() => setButtonHover(true)}
        onMouseLeave={() => setButtonHover(false)}
        onClick={() => {
          setShow(!show);
          onOpen();
        }}
        type="button"
      >
        <IconAppMenu
          width="65%"
          fill={buttonHover || show ? iconFillActive : iconFill}
        />
      </StyledButton>
      <Popup
        show={show}
        width={mobile ? 'calc(100vw - 20px)' : '420px'}
        minWidth="auto"
        top="auto"
        position={mobile ? 'fixed' : 'absolute'}
        left={mobile ? '10px' : 'auto'}
        right={mobile ? '10px' : '0'}
        bottom="unset"
        marginTop="12px"
        anchor="topRight"
        height={popupHeight || 'auto'}
        overflow="auto"
        {...popupProps}
        ref={popupRef}
      >
        <Container
          padding={mobile ? '30px 10px 10px 10px' : '30px 30px 10px 30px'}
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="flex-start"
        >
          <Text
            textAlign="center"
            color="normal"
            fontSize="20px"
            fontFamily="circular"
            fontWeight="bold"
            marginBottom="10px"
            width="100%"
            lineHeight="1.2"
          >
            Your Localsearch Suite
          </Text>
          <Container
            width="100%"
            display="flex"
            flexWrap="wrap"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            {menuItems.map(({ icon, text, href }) => {
              if (hide.includes(text)) return null;
              return (
                <StyledAnchor key={text} href={href} target="_blank">
                  <Container maxWidth="94px" width="100%">
                    {icon}
                  </Container>
                  <Text
                    lineHeight="1.3"
                    marginTop="10px"
                    color="normal"
                    fontFamily="circular"
                    fontWeight="600"
                    fontSize="14px"
                    transition="all 0.3s ease"
                  >
                    {text}
                  </Text>
                </StyledAnchor>
              );
            })}
          </Container>
        </Container>
      </Popup>
    </ClickOutside>
  );
};

AppMenu.propTypes = {
  iconFill: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  iconFillActive: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  hide: PropTypes.arrayOf(PropTypes.string),
  env: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isStaff: PropTypes.bool,
  isPersoniv: PropTypes.bool,
  popupProps: PropTypes.shape({}),
};

AppMenu.defaultProps = {
  iconFill: 'black',
  iconFillActive: '#97AEFF',
  hide: [],
  env: 'production',
  onOpen: () => {},
  onClose: () => {},
  isStaff: false,
  isPersoniv: false,
  popupProps: {},
};

export default AppMenu;
